import { useState } from 'react'
import { useSPD } from 'views/SPDLayout/SPD/SPD.context'
import { useAssignedDigitalTrays } from 'views/DigitalTrayMapping/AssignedDigitalTrays/AssignedDigitalTrays.context'
import { ImplantWithCount } from '../DeleteCustomImplantModal'
import { UsageCustomImplant } from 'views/DigitalTrayMapping/AssignedDigitalTrays/AssignedDigitalTrays.types'
import { enqueueSnackbar } from 'notistack'

const useCustomImplantTrayLogic = ({
  isInOR = false,
}: {
  isInOR?: boolean
}) => {
  const { customImplants, setCustomImplants } = useSPD()
  const { usageCustomImplants, setUsageCustomImplants } =
    useAssignedDigitalTrays()

  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [usageModalOpen, setUsageModalOpen] = useState(false)
  const [selectedImplant, setSelectedImplant] =
    useState<ImplantWithCount | null>(null)
  const [activeTab, setActiveTab] = useState(0)

  // Group implants by deviceId and count them
  const groupedImplants = customImplants.reduce<ImplantWithCount[]>(
    (acc, implant) => {
      const existingImplant = acc.find(
        (item) => item.deviceId === implant.deviceId
      )

      if (existingImplant) {
        existingImplant.count += 1
      } else {
        acc.push({
          deviceId: implant.deviceId,
          deviceDescription: implant.deviceDescription,
          count: 1,
        })
      }

      return acc
    },
    []
  )

  const handleOpenDeleteModal = (implant: ImplantWithCount) => {
    setSelectedImplant(implant)
    setDeleteModalOpen(true)
  }

  const handleOpenUsageModal = (implant: ImplantWithCount) => {
    setSelectedImplant(implant)
    setUsageModalOpen(true)
  }

  const handleCloseDeleteModal = () => {
    setDeleteModalOpen(false)
    setSelectedImplant(null)
  }

  const handleCloseUsageModal = () => {
    setUsageModalOpen(false)
    setSelectedImplant(null)
  }

  const handleRemoveImplant = (count: number) => {
    if (!selectedImplant) return

    const deviceId = selectedImplant.deviceId

    // If removing all instances or only one exists
    if (count >= selectedImplant.count) {
      setCustomImplants(
        customImplants.filter((implant) => implant.deviceId !== deviceId)
      )
    } else {
      // Remove only the specified count
      let remainingToRemove = count
      const updatedImplants = customImplants.filter((implant) => {
        if (implant.deviceId === deviceId && remainingToRemove > 0) {
          remainingToRemove--
          return false
        }
        return true
      })

      setCustomImplants(updatedImplants)
    }
  }

  const handleReturnImplant = (implant: UsageCustomImplant) => {
    // Find the first matching implant in usageCustomImplants
    const implantIndex = usageCustomImplants.findIndex(
      (item) =>
        item.deviceId === implant.deviceId &&
        item.deviceDescription === implant.deviceDescription &&
        item.wasted === implant.wasted
    )

    if (implantIndex !== -1) {
      // Remove only the first matching implant
      const updatedUsageImplants = [
        ...usageCustomImplants.slice(0, implantIndex),
        ...usageCustomImplants.slice(implantIndex + 1),
      ]
      setUsageCustomImplants(updatedUsageImplants)

      // Add back to available implants
      setCustomImplants([
        ...customImplants,
        {
          deviceId: implant.deviceId,
          deviceDescription: implant.deviceDescription,
        },
      ])

      enqueueSnackbar('Implant returned to tray', {
        variant: 'success',
      })
    }
  }

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  return {
    customImplants,
    deleteModalOpen,
    selectedImplant,
    groupedImplants,
    handleOpenDeleteModal,
    handleCloseDeleteModal,
    handleRemoveImplant,
    usageModalOpen,
    handleOpenUsageModal,
    handleCloseUsageModal,
    usageCustomImplants,
    handleReturnImplant,
    activeTab,
    handleTabChange,
  }
}

export default useCustomImplantTrayLogic
